
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { useLanguage } from '@/context/language-context';
import { useAvailability, TimeSlot } from '@/hooks/useAvailability';
import { Loader2, Calendar as CalendarIcon, Clock } from 'lucide-react';

interface BookingDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onBookAppointment: (date: Date, timeSlot: string, duration: number) => Promise<void>;
  escortId: string;
}

const durations = [30, 60, 90, 120, 180, 240];

const BookingDialog: React.FC<BookingDialogProps> = ({
  isOpen,
  onOpenChange,
  onBookAppointment,
  escortId
}) => {
  const { t } = useLanguage();
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [timeSlot, setTimeSlot] = useState<string>("");
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [duration, setDuration] = useState<number>(60);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  
  const { availabilities, isLoading, getAvailableTimeSlots } = useAvailability(escortId);

  useEffect(() => {
    const updateAvailableSlots = async () => {
      if (date) {
        setIsLoadingSlots(true);
        setTimeSlot("");
        const slots = await getAvailableTimeSlots(date, duration);
        console.log('Retrieved slots:', slots);
        setAvailableSlots(slots);
        setIsLoadingSlots(false);
      }
    };
    
    if (isOpen) {
      updateAvailableSlots();
    }
  }, [date, duration, escortId, isOpen, getAvailableTimeSlots]);

  const handleBooking = async () => {
    if (!date || !timeSlot) return;
    
    setIsSubmitting(true);
    try {
      await onBookAppointment(date, timeSlot, duration);
      // Reset form
      setDate(undefined);
      setTimeSlot("");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeDuration = (value: number[]) => {
    setDuration(value[0]);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('messages.book_appointment')}</DialogTitle>
          <DialogDescription>
            {t('Select a date and time for your appointment.')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-4">
          <Calendar
            mode="single"
            selected={date}
            onSelect={setDate}
            className="border rounded-md p-2"
            disabled={(date) => {
              // Disable dates in the past
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return date < today;
            }}
            initialFocus
          />
          
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t('Duration (minutes)')}
            </label>
            <div className="flex items-center space-x-4">
              <Slider
                value={[duration]}
                min={30}
                max={240}
                step={30}
                onValueChange={handleChangeDuration}
                className="flex-1"
              />
              <span className="w-12 text-center font-medium">{duration}</span>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {durations.map((d) => (
                <Button
                  key={d}
                  type="button"
                  size="sm"
                  variant={duration === d ? "default" : "outline"}
                  onClick={() => setDuration(d)}
                  className={duration === d ? "bg-pink-500 hover:bg-pink-600" : ""}
                >
                  {d} min
                </Button>
              ))}
            </div>
          </div>
          
          <div className="space-y-2">
            <label htmlFor="timeSlot" className="text-sm font-medium flex items-center">
              <Clock className="h-4 w-4 mr-2" />
              {t('messages.time')}
            </label>
            
            {isLoadingSlots ? (
              <div className="py-2 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-pink-500 mr-2" />
                <span className="text-sm text-gray-500">Chargement des créneaux...</span>
              </div>
            ) : availableSlots.length > 0 ? (
              <Select value={timeSlot} onValueChange={setTimeSlot}>
                <SelectTrigger id="timeSlot">
                  <SelectValue placeholder={t('Select a time')} />
                </SelectTrigger>
                <SelectContent>
                  {availableSlots.map((slot) => (
                    <SelectItem key={slot.time} value={slot.time}>
                      {slot.time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="p-3 bg-gray-50 rounded-md text-center text-sm text-gray-500">
                {date ? (
                  <span>Aucun créneau disponible pour cette date et cette durée</span>
                ) : (
                  <span>Veuillez sélectionner une date</span>
                )}
              </div>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)} 
            disabled={isSubmitting}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleBooking} 
            disabled={!date || !timeSlot || isSubmitting}
            className="bg-gradient-to-r from-pink-500 to-pink-600"
          >
            {isSubmitting ? t('Sending...') : t('messages.book_appointment')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BookingDialog;
